import type { types as Mediasoup } from "mediasoup-client";

import { resolveConsumerSlot, type ResolveMountSlot } from "./consumer-slots";
import { createVideoConsumerCoalescer, videoCodecPreference } from "./video-consumer-coalescing";
import { createDataConsumerRegistry } from "./data-consumers";
import { STATUS_CODES } from "../status-codes";
import type { StatusCodeValue } from "../status-codes";
import type { ProtooClient } from "./protoo-client";
import type {
  ConsumerClosedData,
  NewConsumerData,
  NewDataConsumerData,
  ProtooNotificationMessage,
  ProtooRequestMessage,
} from "./protoo-messages";
import type { DualVideoPipeline } from "../media/video-pipeline";

/**
 * The registry of video producer↔consumer↔slot mappings, plus the protoo request/
 * notification router that feeds it. Two related-but-separate concerns live next to
 * this one, each in its own file, both wired in below:
 *   - `video-consumer-coalescing.ts` — debounces a join burst of `newConsumer` messages
 *     before this file ever sees them (calls back into `consumeProducer` once settled).
 *   - `data-consumers.ts` — telemetry/chat DataConsumer lifecycle, unrelated to video.
 */

export interface CreateRoomConsumersDeps {
  recvTransportRef: { current: Mediasoup.Transport | null };
  protoo: ProtooClient;
  pipeline: DualVideoPipeline;
  appendDiag: (s: string, opts?: { level?: string }) => void;
  appendSocketIoDcMessage: (m: unknown) => void;
  setStatus: (s: StatusCodeValue) => void;
  onTelemetryOpen?: () => void;
  onTelemetryClose?: () => void;
  videoSlotCoalesceMs?: number;
  resolveMountSlot?: ResolveMountSlot | null;
}

export function createRoomConsumers(ctx: CreateRoomConsumersDeps) {
  const {
    recvTransportRef,
    protoo,
    pipeline,
    appendDiag,
    appendSocketIoDcMessage,
    setStatus,
    onTelemetryOpen,
    onTelemetryClose,
    videoSlotCoalesceMs,
    resolveMountSlot = null,
  } = ctx;

  const consumedProducers = new Set<string>();
  const producerSlot = new Map<string, number>();
  /** consumerId → producerId */
  const consumerToProducer = new Map<string, string>();
  /** slot → producerId currently preferred for that mount */
  const slotProducerId = new Map<number, string>();
  const slotCodecPreference = new Map<number, number>();

  /** Serialize video attach per slot (parallel newConsumer requests). */
  const attachChain: Promise<void>[] = [];

  function chainAttach(slot: number, runAttach: () => Promise<void>): Promise<void> {
    while (attachChain.length <= slot) {
      attachChain.push(Promise.resolve());
    }
    const chained = (attachChain[slot] ?? Promise.resolve()).then(runAttach, runAttach);
    attachChain[slot] = chained;
    return chained;
  }

  /**
   * Skip only true duplicates of the same producerId.
   * A different producerId for the same slot (camera off→on recreates the producer)
   * must replace — skipping ACKs newConsumer without transport.consume() and leaves
   * the UI black forever.
   */
  function shouldSkipDuplicateVideoSlot(slot: number, producerId: string): boolean {
    const existingProducerId = slotProducerId.get(slot);
    if (existingProducerId === producerId) return true;
    if (consumedProducers.has(producerId)) return true;
    if (existingProducerId) {
      appendDiag(
        `replace video consumer slot=${slot + 1} producer ${existingProducerId} → ${producerId}`
      );
    }
    return false;
  }

  /**
   * @param opts.onConsumed — after transport.consume() — ACK newConsumer here so the SFU
   *   can resume RTP. Do not wait for video-element attach / PLI (those run
   *   while the consumer is still paused and delay first frames).
   */
  async function consumeProducer(d: NewConsumerData, opts: { onConsumed?: () => void } = {}) {
    const recvTransport = recvTransportRef.current;
    if (!recvTransport || !d?.producerId) {
      appendDiag(`skip consume: transport=${Boolean(recvTransport)} ${JSON.stringify(d || {})}`);
      return;
    }
    const producerId = String(d.producerId);
    if (consumedProducers.has(producerId)) return;

    if (d.kind === "video") {
      const slot = resolveConsumerSlot(d, producerSlot, appendDiag, resolveMountSlot);
      if (shouldSkipDuplicateVideoSlot(slot, producerId)) return;
      const preference = videoCodecPreference(d);
      if (preference > 0 && (slotCodecPreference.get(slot) ?? 0) > preference) {
        // Reject the extra consumer so the SFU doesn't send an unused copy.
        throw new Error("Prefer original H.264 camera over VP8 compatibility stream");
      }
    }

    const consumerId = d.id || d.consumerId;
    if (!consumerId) throw new Error(`Missing consumer id: ${JSON.stringify(d)}`);

    const consumer = await recvTransport.consume({
      id: consumerId,
      producerId: d.producerId,
      kind: d.kind,
      rtpParameters: d.rtpParameters,
      appData: d.appData || {},
    });
    consumedProducers.add(producerId);
    consumerToProducer.set(String(consumer.id), producerId);
    appendDiag(`consume ${d.kind} producer=${producerId} id=${consumer.id}`);
    opts.onConsumed?.();

    if (consumer.kind === "video") {
      const slot = resolveConsumerSlot(d, producerSlot, appendDiag, resolveMountSlot);
      slotProducerId.set(slot, producerId);
      slotCodecPreference.set(slot, videoCodecPreference(d));
      const runAttach = async () => {
        await pipeline.attachVideoConsumer(
          consumer,
          slot,
          producerId,
          { setStatus },
          {
            beforeReplace: (prev) => {
              try {
                if (prev.producerId) {
                  const pid = String(prev.producerId);
                  producerSlot.delete(pid);
                  consumedProducers.delete(pid);
                  consumerToProducer.delete(String(prev.id));
                }
              } catch {
                /* ignore */
              }
            },
          }
        );
      };
      await chainAttach(slot, runAttach);
    }
  }

  const videoCoalescer = createVideoConsumerCoalescer({
    protoo,
    appendDiag,
    resolveMountSlot,
    videoSlotCoalesceMs,
    producerSlot,
    consume: consumeProducer,
  });

  const dataConsumerRegistry = createDataConsumerRegistry({
    appendDiag,
    appendSocketIoDcMessage,
    onTelemetryOpen,
    onTelemetryClose,
  });

  async function handleProtooRequest(msg: ProtooRequestMessage) {
    const recvTransport = recvTransportRef.current;

    if (msg.method === "newConsumer") {
      if (!recvTransport) {
        protoo.sendResponse(msg.id, false, {}, "Consumer transport not ready", 503);
        return;
      }
      setStatus(STATUS_CODES.CONSUMER_ATTACHING);
      // The union's open fallback arm (any other method, see protoo-messages.ts) shares
      // the `method: string` field, so the `=== "newConsumer"` check above can't narrow
      // `msg.data`'s type on its own — asserting it here is a direct consequence of the
      // runtime check just made, not a blind cast.
      const nd = msg.data as NewConsumerData;
      appendDiag(
        `newConsumer ${nd.kind} producer=${nd.producerId} peer=${nd.producerPeerId ?? nd.peerId ?? "-"}`
      );
      if (nd.kind === "video") {
        videoCoalescer.scheduleVideoConsumer(msg, true);
        return;
      }
      // Isolated the same way the video path already is (via the coalescer, above):
      // one bad non-video consumer (e.g. audio) must only NACK this one request, not
      // escalate to a session-wide FAILED — a transient `consumeProducer` reject here
      // used to propagate straight past this function into operator-session.ts's
      // top-level onRequest catch, which tore down the WHOLE session over it.
      try {
        await consumeProducer(nd);
        protoo.sendResponse(msg.id, true, {});
      } catch (err) {
        appendDiag(`consumeProducer ${nd.kind} failed: ${err}`, { level: "warn" });
        protoo.sendResponse(msg.id, false, {}, String(err), 500);
      }
      return;
    }

    if (msg.method === "newDataConsumer") {
      if (!recvTransport) {
        protoo.sendResponse(msg.id, false, {}, "Consumer transport not ready", 503);
        return;
      }
      // Same isolation as the non-video branch above — a telemetry/chat data-consumer
      // hiccup (e.g. a transient SCTP setup reject right after a rejoin, while the recv
      // transport is still settling) must not fail the whole session either.
      try {
        await dataConsumerRegistry.handleNewDataConsumer(
          recvTransport,
          msg.data as NewDataConsumerData
        );
        protoo.sendResponse(msg.id, true, {});
      } catch (err) {
        appendDiag(`newDataConsumer failed: ${err}`, { level: "warn" });
        protoo.sendResponse(msg.id, false, {}, String(err), 500);
      }
      return;
    }

    protoo.sendResponse(msg.id, false, {}, `Unsupported: ${msg.method}`, 400);
  }

  async function handleProtooNotification(msg: ProtooNotificationMessage) {
    if (msg.method === "consumerClosed") {
      const data = (msg.data ?? {}) as ConsumerClosedData;
      const consumerId = data.consumerId;
      if (consumerId) {
        const producerId = consumerToProducer.get(String(consumerId));
        if (producerId) {
          consumedProducers.delete(producerId);
          producerSlot.delete(producerId);
          consumerToProducer.delete(String(consumerId));
          for (const [slot, pid] of slotProducerId.entries()) {
            if (pid === producerId) {
              slotProducerId.delete(slot);
              slotCodecPreference.delete(slot);
            }
          }
        }
        // Ensure pipeline slot is cleared even if the mediasoup "close" event races
        // behind a subsequent newConsumer (camera off→on).
        pipeline.dropConsumerById?.(consumerId, "consumerClosed");
      }
      return;
    }

    if (msg.method !== "newConsumer") return;
    appendDiag("protoo notification: newConsumer (async)");
    setStatus(STATUS_CODES.CONSUMER_ATTACHING);
    const nd = msg.data as NewConsumerData;
    if (nd.kind === "video") {
      videoCoalescer.scheduleVideoConsumer(msg, false);
      return;
    }
    // Same isolation as the request-side branch above — this is a fire-and-forget
    // notification (no protoo response to NACK), so there's nothing to send back, but a
    // reject here must still not propagate into operator-session.ts's top-level
    // onNotification catch and fail the whole session over one bad non-video consumer.
    try {
      await consumeProducer(nd);
    } catch (err) {
      appendDiag(`consumeProducer ${nd.kind} failed: ${err}`, { level: "warn" });
    }
  }

  function resetForRejoin() {
    videoCoalescer.clearVideoCoalesce();
    dataConsumerRegistry.closeDataConsumers();
    consumedProducers.clear();
    consumerToProducer.clear();
    slotProducerId.clear();
    slotCodecPreference.clear();
    producerSlot.clear();
  }

  return { handleProtooRequest, handleProtooNotification, resetForRejoin };
}
